import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import { PersonalNumberRange } from 'features/care-connect-settings/domain/models/personal-number-range';
import {
  useReadPersonalNumberRanges,
  useRemovePersonalNumberRange,
  useUpsertPersonalNumberRange,
} from 'features/care-connect-settings/domain/queries/personal-number-range-queries';

export type PersonalNumberRangeDetails = {
  personalNumberRanges: PersonalNumberRange[] | undefined;
  readPersonalNumberRangesIsLoading: boolean;
  isEditing: boolean;
  shouldShowConfirmationPopup: boolean;
  setIsEditing: (isEditing: boolean) => void;
  setShouldShowConfirmationPopup: (shouldShow: boolean) => void;
  cancelEditing: () => void;
  addPersonalNumberRange: () => void;
  removePersonalNumberRange: (personalNumberRange: PersonalNumberRange) => void;
  upsertPersonalNumberRange: (personalNumberRange: PersonalNumberRange, onSuccess: () => void) => void;
};

const usePersonalNumberRangeDetails = (isRootSelected: boolean): PersonalNumberRangeDetails => {
  const { t } = useTranslation('careConnectSettings');
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { showConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const [personalNumberRanges, setPersonalNumberRanges] = useState<PersonalNumberRange[] | undefined>();
  const [isEditing, setIsEditing] = useState(false);
  const [shouldShowConfirmationPopup, setShouldShowConfirmationPopup] = useState(false);

  const {
    data: readPersonalNumberRangesData,
    isLoading: readPersonalNumberRangesIsLoading,
    error: readPersonalNumberRangesError,
  } = useReadPersonalNumberRanges(isRootSelected);

  const { mutate: upsertMutation } = useUpsertPersonalNumberRange();
  const { mutate: removeMutation } = useRemovePersonalNumberRange();

  useEffect(() => {
    if (readPersonalNumberRangesError) {
      setErrorMessage(readPersonalNumberRangesError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readPersonalNumberRangesError]);

  useEffect(() => {
    if (readPersonalNumberRangesData) {
      setPersonalNumberRanges(getSortedPersonalNumberRanges(readPersonalNumberRangesData.personalNumberRanges));
      setIsEditing(false);
    }
  }, [readPersonalNumberRangesData]);

  const cancelEditing = (): void => {
    setPersonalNumberRanges((current) => current?.filter((x) => x.id));
    setIsEditing(false);
    setShouldShowConfirmationPopup(false);
    enablePopUpGuard(false);
  };

  const addPersonalNumberRange = (): void => {
    if (personalNumberRanges) {
      const newRange: PersonalNumberRange = {
        id: undefined,
        lowerBound: undefined,
        upperBound: undefined,
        key: uuidv4(),
      };
      setPersonalNumberRanges([...personalNumberRanges, newRange]);
      setIsEditing(true);
    }
  };

  const confirmRemovePersonalNumberRange = (personalNumberRange: PersonalNumberRange): void => {
    removeMutation(personalNumberRange.id!, {
      onSuccess: () => {
        setPersonalNumberRanges((current) => current?.filter((x) => x !== personalNumberRange));
      },
      onError: (error: Error) => {
        setErrorMessage(error);
      },
    });
  };

  const removePersonalNumberRange = (personalNumberRange: PersonalNumberRange): void => {
    if (personalNumberRange.id) {
      showConfirmationPopUp(
        () => confirmRemovePersonalNumberRange(personalNumberRange),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.title'),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.text'),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.confirmDelete'),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.cancel'),
      );
    } else if (personalNumberRanges) {
      setPersonalNumberRanges(personalNumberRanges.filter((x) => x !== personalNumberRange));
      setIsEditing(false);
    }
  };

  const confirmUpsertPersonalNumberRange = (personalNumberRange: PersonalNumberRange, onSuccess: () => void): void => {
    if (personalNumberRange.lowerBound === undefined || personalNumberRange.upperBound === undefined) return;

    upsertMutation(
      { id: personalNumberRange.id, lowerBound: personalNumberRange.lowerBound, upperBound: personalNumberRange.upperBound },
      {
        onSuccess: (upserted) => {
          setPersonalNumberRanges(
            getSortedPersonalNumberRanges(
              (personalNumberRanges ?? []).map((x) =>
                (personalNumberRange.id ? x.id === upserted.id : !x.id) ? { ...upserted, key: x.key } : x,
              ),
            ),
          );
          setIsEditing(false);
          setShouldShowConfirmationPopup(false);
          onSuccess();
        },
        onError: (error: Error) => {
          setErrorMessage(error);
        },
      },
    );
  };

  const upsertPersonalNumberRange = (personalNumberRange: PersonalNumberRange, onSuccess: () => void): void => {
    if (personalNumberRange.lowerBound === undefined || personalNumberRange.upperBound === undefined) return;

    const existing = personalNumberRanges?.find((x) => x.id && x.id === personalNumberRange.id);

    if (existing && isRangeShrinking(existing, personalNumberRange.lowerBound, personalNumberRange.upperBound)) {
      showConfirmationPopUp(
        () => confirmUpsertPersonalNumberRange(personalNumberRange, onSuccess),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.title'),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.text'),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.confirmUpdate'),
        t('personalNumberRangeDetails.confirmationPopups.personalNumberRange.cancel'),
      );
    } else {
      confirmUpsertPersonalNumberRange(personalNumberRange, onSuccess);
    }
  };

  const isRangeShrinking = (existing: PersonalNumberRange, lowerBound: number, upperBound: number): boolean =>
    lowerBound > (existing.lowerBound ?? 0) || upperBound < (existing.upperBound ?? 0);

  const getSortedPersonalNumberRanges = (ranges: PersonalNumberRange[]): PersonalNumberRange[] =>
    [...ranges].sort((a, b) => (a.lowerBound ?? 0) - (b.lowerBound ?? 0));

  return {
    personalNumberRanges,
    readPersonalNumberRangesIsLoading,
    isEditing,
    shouldShowConfirmationPopup,
    setIsEditing,
    setShouldShowConfirmationPopup,
    cancelEditing,
    addPersonalNumberRange,
    removePersonalNumberRange,
    upsertPersonalNumberRange,
  };
};

export default usePersonalNumberRangeDetails;
